import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Tooltip,
  Modal,
  DatePicker,
  Tag
} from 'antd';
import {
  EditFilled,
  DeleteFilled,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeFilled
} from '@ant-design/icons';
import BatteryGauge from 'react-battery-gauge';
import {
  apiDeleteReplacement,
  apiGetAllReplacements,
  apiGetDevicesInformation
} from '@services/ManageDevices/DeviceServices';
import { showError, showWarning } from '@components/notification';
import {
  communityList,
  convertLocaltimeToUTC,
  downloadBlobFile,
  formatCurrency
} from '@utils/utils';
import { useNavigate } from 'react-router-dom';
import ModalImportFile from './ModalImportFile';
import { URL_API } from '@configs/index';
import moment from 'moment';
import { apiDeleteCustomer, apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';

import { TYPES } from '../Add/components/Add';
import { usePermission } from '@contexts/Auth/UsePermission';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title, Paragraph } = Typography;

const { RangePicker } = DatePicker;

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('ALL');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [replacementType, setReplacementType] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [visibleModalImport, setVisibleModalImport] = useState(false);

  const [dateRange, setDateRange] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const { replacement, isSuperAdmin } = usePermission();
  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetAllReplacements({
        page: currentPage,
        size: pageSize,
        keyWord: search?.trim() ?? undefined,
        serialNo: serialNo?.trim() ?? undefined,
        type: replacementType,
        fromDate: dateRange?.length
          ? moment(new Date(dateRange?.[0])).format('YYYY-MM-DD')
          : undefined,
        toDate: dateRange?.length
          ? moment(new Date(dateRange?.[1])).format('YYYY-MM-DD')
          : undefined
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  const handleEdit = (record) => {
    navigate(`/replacement/update/${record.id}`);
  };

  const handleViewDetail = (record) => {
    navigate(`/replacement/detail/${record.id}`);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xoá sự kiện thay thế này?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await apiDeleteReplacement(record?.id);
        fetchData();
      },
      onCancel() {
        console.log('Cancel deletion');
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 70,
        fixed: 'left'
      },
      {
        title: 'DBĐD',
        dataIndex: 'customer_id',
        fixed: 'left',
        width: 80,
        render: (text, record) => (
          <p style={{ color: 'var(--link)', margin: 0 }}>
            {`${record?.customer_id}
          `}
          </p>
        )
      },
      {
        title: 'Tên khách hàng',
        dataIndex: 'customer_name',
        fixed: 'left',
        width: 200,
        render: (text, record) => (
          <p style={{ color: 'var(--link)', margin: 0 }}>
            {`${record?.customer_name}
          `}
          </p>
        )
      },
      {
        title: 'Loại',
        dataIndex: 'type',
        fixed: 'left',
        render: (text, record) => {
          return <>{TYPES?.find((item) => item.value === record?.type)?.label}</>;
        },
        width: 150
      },
      {
        title: 'Thời điểm thực hiện',
        dataIndex: 'replacement_date',
        render: (text, record) => {
          return <>{moment(record?.replacement_date).format('DD/MM/YYYY HH:mm')}</>;
        },
        width: 100
      },
      {
        title: 'Module cũ',
        children: [
          {
            title: 'Số Serial',
            dataIndex: 'serial_no_old',
            width: 120
          },
          {
            title: 'Model',
            dataIndex: 'model_old',
            width: 100
          }
        ]
      },
      {
        title: 'Đồng hồ cũ',
        children: [
          {
            title: 'Seri DH',
            dataIndex: 'meter_serial_no_old',
            width: 120,
            render: (val) => val || '-'
          },
          {
            title: 'Tên DH',
            dataIndex: 'meter_name_old',
            width: 120,
            render: (val) => val || '-'
          },
          {
            title: 'Cỡ DH',
            dataIndex: 'meter_size_old',
            width: 80,
            render: (val) => val || '-'
          },
          {
            title: 'Chỉ số cũ (L)',
            dataIndex: 'meter_reading_old',
            width: 120,
            render: (val) => (val ? formatCurrency(val) : '-')
          }
        ]
      },
      {
        title: 'Module mới',
        children: [
          {
            title: 'Số Serial',
            dataIndex: 'serial_no_new',
            width: 120
          },
          {
            title: 'Model',
            dataIndex: 'model_new',
            width: 100
          }
        ]
      },
      {
        title: 'Đồng hồ mới',
        children: [
          {
            title: 'Seri DH',
            dataIndex: 'meter_serial_no_new',
            width: 120,
            render: (val) => val || '-'
          },
          {
            title: 'Tên DH',
            dataIndex: 'meter_name_new',
            width: 120,
            render: (val) => val || '-'
          },
          {
            title: 'Cỡ DH',
            dataIndex: 'meter_size_new',
            width: 80,
            render: (val) => val || '-'
          },
          {
            title: 'Chỉ số mới (L)',
            dataIndex: 'meter_reading_new',
            width: 120,
            render: (val) => (val ? formatCurrency(val) : '-')
          }
        ]
      },
      {
        title: 'Ghi chú',
        dataIndex: 'reason',
        width: 200
      },
      {
        title: 'Người tạo',
        dataIndex: 'created_by',
        width: 100,
        render: (text, record) => <>{record?.created_by || 'N/A'}</>
      },
      {
        title: 'Hình ảnh',
        dataIndex: 'image',
        width: 200,
        render: (_, record) => {
          const isMeterEvent = [
            'INSTALL_METER',
            'REPLACE_METER',
            'REPLACE_ALL',
            'REMOVE_METER'
          ].includes(record.type);
          const isModuleEvent = ['NEW', 'REPLACE', 'REPLACE_ALL', 'REMOVE'].includes(record.type);
          const link = (url, label) =>
            url ? (
              <div>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#0284c7', textDecoration: 'underline' }}>
                  {label}
                </a>
              </div>
            ) : null;
          return (
            <div>
              {record.old_device_image?.url &&
                link(record.old_device_image?.url, 'Ảnh thiết bị cũ')}
              {record.new_device_image?.url &&
                link(record.new_device_image?.url, 'Ảnh thiết bị mới')}
              {record.meter_serial_image_old?.url &&
                link(record.meter_serial_image_old?.url, 'Ảnh seri ĐH cũ')}
              {record.meter_reading_old_image?.url &&
                link(record.meter_reading_old_image?.url, 'Ảnh chỉ số ĐH cũ')}
              {record.meter_serial_image_new?.url &&
                link(record.meter_serial_image_new?.url, 'Ảnh seri ĐH mới')}
              {record.meter_reading_new_image?.url &&
                link(record.meter_reading_new_image?.url, 'Ảnh chỉ số ĐH mới')}
            </div>
          );
        }
      },
      ...(isSuperAdmin
        ? [
            {
              title: 'Trạng thái',
              dataIndex: 'status',
              width: 120,
              render: (text, record) => {
                const status =
                  record?.end_date && moment(record?.end_date).isBefore(moment())
                    ? 'INACTIVE'
                    : 'ACTIVE';
                return <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>{status}</Tag>;
              }
            }
          ]
        : []),
      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            <Tooltip title="Xem chi tiết">
              <Button
                type="text"
                shape="circle"
                icon={<EyeFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            {replacement.edit && (
              <Tooltip title="Chỉnh sửa">
                <Button
                  type="text"
                  shape="circle"
                  icon={<EditFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            )}
            {replacement.delete && (
              <Tooltip title="Xoá">
                <Button
                  type="danger"
                  shape="circle"
                  icon={<DeleteFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            )}
          </Space>
        )
      }
    ],
    [isSuperAdmin, replacement.edit, replacement.delete]
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSerialNoChange = (e) => {
    setSerialNo(e.target.value);
  };

  const handleReplacementTypeChange = (value) => {
    setReplacementType(value);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const onChangeDateRange = (dates, dateStrings) => {
    setDateRange(dates);
  };

  const handleResetSearch = () => {
    setSearch('');
    setReplacementType(undefined);
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/replacements/export-report`,
        'Danh sách thông tin thay thế.xlsx',
        {
          keyWord: search?.trim() ?? undefined,
          serialNo: serialNo?.trim() ?? undefined,
          type: replacementType,
          fromDate: dateRange?.length
            ? convertLocaltimeToUTC(dateRange?.[0], '00:00:00.000')
            : undefined,
          toDate: dateRange?.length
            ? convertLocaltimeToUTC(dateRange?.[1], '23:59:59.999')
            : undefined
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = () => {
    navigate('/replacement/add');
  };

  return (
    <div className="dashboard">
      <ModalImportFile
        visible={visibleModalImport}
        onCancel={() => {
          setVisibleModalImport(false);
          fetchData();
        }}
      />
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4}>Quản lý thay thế lắp mới</Typography.Title>
        </Col>
        <Col>
          <Space>
            {replacement.create && (
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddDevice}
                style={{
                  color: '#0284c7',
                  height: '36px',
                  backgroundColor: '#eef1f6',
                  borderColor: '#eef1f6',
                  fontSize: '18px'
                }}>
                Thêm mới sự kiện
              </Button>
            )}
            {replacement.export && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportExcelButtonClick}
                loading={loading}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0284c7',
                  borderColor: '#0284c7',
                  fontSize: '18px'
                }}>
                Xuất file excel
              </Button>
            )}

            {replacement.import && (
              <Button
                icon={<UploadOutlined />}
                onClick={() => {
                  setVisibleModalImport(true);
                }}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0284c7',
                  borderColor: '#0284c7',
                  fontSize: '18px'
                }}>
                Nhập file excel
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      <Card bodyStyle={{ padding: '8px' }}>
        <Row gutter={[8, 8]}>
          {/* <Col md={6} xs={12}>
            <Select
              value={selectedCommunity}
              onChange={setSelectedCommunity}
              style={{ width: '100%', height: '36px' }}>
              {communityList.map((community) => (
                <Option key={community.value} value={community.value}>
                  {community.label}
                </Option>
              ))}
            </Select>
          </Col> */}
          <Col md={5} xs={12}>
            <Input
              value={search}
              onChange={handleSearchChange}
              onPressEnter={handleSearch}
              placeholder="Tìm kiếm tên, mã kh..."
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col md={5} xs={12}>
            <Input
              style={{ height: '36px', width: '100%' }}
              size="small"
              placeholder="Tìm kiếm theo số serial, imei..."
              value={serialNo}
              onChange={handleSearchSerialNoChange}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col md={5} xs={12}>
            <Select
              allowClear
              placeholder="Chọn loại sự kiện"
              value={replacementType}
              onChange={handleReplacementTypeChange}
              style={{ width: '100%', height: '36px', fontSize: '18px' }}>
              {TYPES.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col md={5} sm={12}>
            <RangePicker
              value={dateRange}
              onChange={onChangeDateRange}
              style={{ width: '100%', height: '36px', fontSize: '18px' }}
              format="YYYY-MM-DD"
              placeholder={['Từ ngày', 'Đến ngày']}
            />
          </Col>
          <Col md={4} sm={12}>
            <Button
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{
                backgroundColor: '#0284c7',
                borderColor: '#0284c7',
                color: '#fff',
                height: '36px',
                fontSize: '18px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
      </Card>
      <Table
        bordered
        // size="middle"
        loading={isFetching}
        dataSource={deviceList || []}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          onChange: handleChangePage,
          onShowSizeChange: handleChangePageSize,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
        }}
        style={{
          borderRadius: 8,
          thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
          fontSize: '30px'
        }}
        scroll={{ x: 'max-content', y: 600 }}
      />
    </div>
  );
}
